import React, { useEffect, useState } from "react";
import { apiClient } from "../../services/apiClientService";
import { CheckCircle2, Loader2, MapPin, Store } from "lucide-react";
import toast from "react-hot-toast";
import { useUserAuth } from "../../context/UserAuthContext";
import { isValidEmail } from "../../utils/validators";

const AddressSkeleton = () => (
  <div className="space-y-2" aria-hidden>
    <div className="h-24 animate-pulse rounded-2xl bg-stone-200/70" />
    <div className="h-24 animate-pulse rounded-2xl bg-stone-200/50" />
  </div>
);

const ShippingInfo = ({
  form,
  setForm,
  selectedIndex,
  setSelectedIndex,
  onAddressSelected,
  receivingMethod = "delivery",
  onReceivingMethodChange,
  /** From storefront: `both` = show deliver vs pickup choice; otherwise locked. */
  customerFulfillmentMode = "delivery",
  pickupPointLocation = "",
  pickupPointGoogleMapsUrl = "",
  pickupPointLat = null,
  pickupPointLng = null,
}) => {
  const { isAuthenticated } = useUserAuth();
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [loadState, setLoadState] = useState("idle");
  const [isSaving, setIsSaving] = useState(false);
  /** When saving a new address from checkout, also mark it primary. */
  const [makePrimaryOnSave, setMakePrimaryOnSave] = useState(false);
  const [showAllAddresses, setShowAllAddresses] = useState(false);

  const ADDRESS_PREVIEW_COUNT = 2;

  const emptyAddress = {
    fullName: "",
    email: "",
    phone: "",
    country: "India",
    state: "",
    city: "",
    address: "",
  };

  useEffect(() => {
    let cancelled = false;

    const fetchAddresses = async () => {
      if (customerFulfillmentMode === "pickup") {
        setSavedAddresses([]);
        setShowNewAddressForm(false);
        setLoadState("done");
        return;
      }
      if (!isAuthenticated) {
        setSavedAddresses([]);
        setShowNewAddressForm(true);
        setLoadState("done");
        return;
      }
      setLoadState("loading");
      try {
        const res = await apiClient("GET", "/api/users/shipping-addresses", null, {
          skipLoading: true,
        });
        if (cancelled) return;
        const list = res?.addresses ?? [];
        setSavedAddresses(list);
        if (list.length === 0) {
          setShowNewAddressForm(true);
        } else {
          setShowNewAddressForm(false);
          const primaryIdx = list.findIndex((a) => a.isPrimary);
          const idx = primaryIdx >= 0 ? primaryIdx : 0;
          setSelectedIndex(idx);
          setForm(list[idx]);
          onAddressSelected?.(list[idx], true);
        }
      } catch {
        if (cancelled) return;
        setSavedAddresses([]);
        setShowNewAddressForm(true);
        toast.error("Could not load saved addresses. Add a delivery address below.");
      } finally {
        if (!cancelled) setLoadState("done");
      }
    };

    fetchAddresses();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, customerFulfillmentMode]);

  const handleSelect = (idx) => {
    setSelectedIndex(idx);
    const addr = savedAddresses[idx];
    setForm(addr);
    setShowNewAddressForm(false);
    onAddressSelected?.(addr, true);
  };

  const handleAddAddress = async () => {
    if (!form.fullName?.trim() || !form.address?.trim() || !form.phone?.trim()) {
      toast.error("Please fill name, phone, and street address.");
      return;
    }
    if (form.email?.trim() && !isValidEmail(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!isAuthenticated) {
      toast.error("Please log in to save an address.");
      return;
    }
    setIsSaving(true);
    try {
      const res = await apiClient(
        "POST",
        "/api/users/shipping-addresses",
        { ...form, isPrimary: makePrimaryOnSave },
        {
          skipLoading: true,
        },
      );
      if (res?.addresses) {
        setSavedAddresses(res.addresses);
        setShowNewAddressForm(false);
        setMakePrimaryOnSave(false);
        const list = res.addresses;
        const primaryIdx = list.findIndex((a) => a.isPrimary);
        const idx = primaryIdx >= 0 ? primaryIdx : list.length - 1;
        const chosen = list[idx];
        setSelectedIndex(idx);
        setForm(chosen);
        onAddressSelected?.(chosen, true);
        toast.success("Address saved");
      }
    } catch {
      toast.error("Could not save address. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const showDualChoice = customerFulfillmentMode === "both";
  const pickupFlow =
    customerFulfillmentMode === "pickup" ||
    (showDualChoice && receivingMethod === "pickup");

  const mapEmbedUrl = (() => {
    const link = String(pickupPointGoogleMapsUrl || "").trim();
    if (typeof pickupPointLat === "number" && typeof pickupPointLng === "number") {
      return `https://www.google.com/maps?q=${encodeURIComponent(
        `${pickupPointLat},${pickupPointLng}`,
      )}&output=embed`;
    }
    if (link) {
      // Best-effort embed for google.com/maps links (short maps.app.goo.gl can't embed reliably).
      try {
        const u = new URL(link);
        if (u.hostname.toLowerCase().includes("google.") && u.pathname.includes("/maps")) {
          if (!u.searchParams.get("output")) u.searchParams.set("output", "embed");
          return u.toString();
        }
      } catch {
        // ignore
      }
    }
    if (pickupPointLocation) {
      return `https://www.google.com/maps?q=${encodeURIComponent(pickupPointLocation)}&output=embed`;
    }
    return "";
  })();

  const openMapsUrl = (() => {
    const link = String(pickupPointGoogleMapsUrl || "").trim();
    if (link) return link;
    if (typeof pickupPointLat === "number" && typeof pickupPointLng === "number") {
      return `https://www.google.com/maps?q=${encodeURIComponent(
        `${pickupPointLat},${pickupPointLng}`,
      )}`;
    }
    if (pickupPointLocation) {
      return `https://www.google.com/maps?q=${encodeURIComponent(pickupPointLocation)}`;
    }
    return "";
  })();

  return (
    <div className="space-y-4">
      {showDualChoice ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => onReceivingMethodChange?.("delivery")}
            className={`flex min-h-[52px] items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left transition ${
              receivingMethod === "delivery"
                ? "border-orange-500 bg-orange-50/80 shadow-md ring-1 ring-orange-200"
                : "border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50/80"
            }`}
          >
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                receivingMethod === "delivery"
                  ? "bg-orange-500 text-white"
                  : "bg-stone-100 text-stone-600"
              }`}
            >
              <MapPin className="h-5 w-5" strokeWidth={2} />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-bold text-stone-900">Deliver to address</span>
              <span className="block text-xs text-stone-500">Usual fees apply</span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => onReceivingMethodChange?.("pickup")}
            className={`flex min-h-[52px] items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left transition ${
              receivingMethod === "pickup"
                ? "border-emerald-500 bg-emerald-50/80 shadow-md ring-1 ring-emerald-200"
                : "border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50/80"
            }`}
          >
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                receivingMethod === "pickup"
                  ? "bg-emerald-600 text-white"
                  : "bg-stone-100 text-stone-600"
              }`}
            >
              <Store className="h-5 w-5" strokeWidth={2} />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-bold text-stone-900">Self pickup</span>
              <span className="block text-xs text-stone-500">No delivery fee</span>
            </span>
          </button>
        </div>
      ) : null}

      {pickupFlow ? (
        <div className="rounded-2xl border border-emerald-200/90 bg-emerald-50/60 px-4 py-3 text-sm text-emerald-950">
          <p className="font-semibold">Collect from the seller</p>
          <p className="mt-1 text-emerald-900/90">
            You don’t need a delivery address. Any delivery or packing charges for shipping are waived
            for this order.
          </p>
          {pickupPointLocation ? (
            <p className="mt-2 text-emerald-950">
              <span className="font-semibold">Pickup point:</span>{" "}
              {pickupPointLocation}
            </p>
          ) : null}
          {openMapsUrl ? (
            <a
              href={openMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex text-sm font-bold text-emerald-800 underline decoration-emerald-400 underline-offset-4 hover:text-emerald-900"
            >
              Open in Google Maps
            </a>
          ) : null}
          {mapEmbedUrl ? (
            <div className="mt-3 overflow-hidden rounded-xl border border-emerald-200 bg-white">
              <iframe
                title="Pickup point map"
                src={mapEmbedUrl}
                className="h-48 w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {loadState === "loading" ? (
        <div className="rounded-2xl border border-stone-100 bg-stone-50/80 p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-stone-600">
            <Loader2 className="h-4 w-4 animate-spin text-orange-500" aria-hidden />
            Loading your addresses…
          </div>
          <AddressSkeleton />
        </div>
      ) : null}

      {pickupFlow ? null : loadState !== "loading" && savedAddresses.length > 0 ? (
        <>
          <h3 className="text-sm font-bold uppercase tracking-wide text-stone-500">
            Saved addresses
          </h3>
          <div className="space-y-2">
            {(showAllAddresses ? savedAddresses : savedAddresses.slice(0, ADDRESS_PREVIEW_COUNT)).map((addr, idx) => (
              <button
                key={idx}
                type="button"
                className={`relative w-full cursor-pointer rounded-2xl border-2 p-4 text-left transition duration-200 ${
                  selectedIndex === idx
                    ? "border-orange-500 bg-orange-50/70 shadow-md ring-1 ring-orange-200"
                    : "border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50/80"
                }`}
                onClick={() => handleSelect(idx)}
              >
                {selectedIndex === idx && (
                  <div className="absolute right-3 top-3 text-orange-600">
                    <CheckCircle2 className="h-5 w-5" />
                  </div>
                )}
                <p className="pr-8 font-semibold text-stone-900">
                  {addr.fullName}
                  {addr.isPrimary ? (
                    <span className="ml-2 inline-block rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-orange-800">
                      Primary
                    </span>
                  ) : null}
                </p>
                <p className="mt-1 text-sm text-stone-600">
                  {addr.address}, {addr.city}, {addr.state}, {addr.country}
                </p>
                <p className="mt-1 text-xs text-stone-500">
                  {addr.phone} · {addr.email}
                </p>
              </button>
            ))}
          </div>
          {savedAddresses.length > ADDRESS_PREVIEW_COUNT && (
            <button
              type="button"
              onClick={() => setShowAllAddresses((v) => !v)}
              className="flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700 transition"
            >
              {showAllAddresses ? (
                <>
                  <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832l-3.71 3.938a.75.75 0 01-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z" clipRule="evenodd"/></svg>
                  Show less
                </>
              ) : (
                <>
                  <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd"/></svg>
                  {savedAddresses.length - ADDRESS_PREVIEW_COUNT} more address{savedAddresses.length - ADDRESS_PREVIEW_COUNT > 1 ? "es" : ""}
                </>
              )}
            </button>
          )}
        </>
      ) : null}

      {pickupFlow ? null : loadState !== "loading" ? (
        <button
          type="button"
          className="mt-1 flex w-full min-h-[48px] items-center justify-center rounded-2xl border-2 border-dashed border-orange-300 bg-orange-50/50 py-3 text-sm font-bold text-orange-800 transition active:scale-[0.99] hover:bg-orange-100/80 sm:w-auto sm:px-6"
            onClick={() => {
              setShowNewAddressForm(true);
              setSelectedIndex(null);
              setMakePrimaryOnSave(false);
              // Always reset the form so "add again" works reliably.
              setForm(emptyAddress);
              onAddressSelected?.(emptyAddress, false);
            }}
        >
          + Deliver to a new address
        </button>
      ) : null}

      {pickupFlow ? null : showNewAddressForm && loadState !== "loading" ? (
        <div className="mt-4 grid grid-cols-1 gap-3 rounded-2xl border border-stone-200 bg-stone-50/50 p-4 md:grid-cols-2 md:gap-4">
          <p className="text-sm font-semibold text-stone-800 md:col-span-2">
            New delivery address
          </p>
          <input
            name="fullName"
            placeholder="Full name"
            value={form.fullName}
            onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            className="min-h-[44px] rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-base outline-none ring-orange-400/30 transition focus:border-orange-400 focus:ring-2 sm:text-sm"
            autoComplete="name"
          />
          <input
            name="email"
            placeholder="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="min-h-[44px] rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-base outline-none ring-orange-400/30 transition focus:border-orange-400 focus:ring-2 sm:text-sm"
            autoComplete="email"
          />
          <input
            name="phone"
            placeholder="Phone"
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="min-h-[44px] rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-base outline-none ring-orange-400/30 transition focus:border-orange-400 focus:ring-2 sm:text-sm"
            autoComplete="tel"
          />
          <select
            name="country"
            value={form.country}
            onChange={(e) => setForm({ ...form, country: e.target.value })}
            className="shop-select-control min-h-[44px] rounded-xl border border-stone-200 bg-white py-2.5 pl-3 text-base text-stone-900 outline-none ring-orange-400/30 transition focus:border-orange-400 focus:ring-2 sm:text-sm"
          >
            <option>India</option>
            <option>Afghanistan</option>
          </select>
          <input
            name="state"
            placeholder="State"
            value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })}
            className="min-h-[44px] rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-base outline-none ring-orange-400/30 transition focus:border-orange-400 focus:ring-2 sm:text-sm"
            autoComplete="address-level1"
          />
          <input
            name="city"
            placeholder="City / area"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            className="min-h-[44px] rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-base outline-none ring-orange-400/30 transition focus:border-orange-400 focus:ring-2 sm:text-sm"
            autoComplete="address-level2"
          />
          <textarea
            name="address"
            placeholder="Flat, street, landmark"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            className="min-h-[100px] rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-base outline-none ring-orange-400/30 transition focus:border-orange-400 focus:ring-2 md:col-span-2 sm:text-sm"
            autoComplete="street-address"
          />

          <label className="flex cursor-pointer items-center gap-2 md:col-span-2">
            <input
              type="checkbox"
              checked={makePrimaryOnSave}
              onChange={(e) => setMakePrimaryOnSave(e.target.checked)}
              className="h-4 w-4 rounded border-stone-300 text-orange-600 focus:ring-orange-500"
            />
            <span className="text-sm text-stone-700">
              Make this my <strong>primary</strong> address (auto-selected at checkout)
            </span>
          </label>

          <div className="md:col-span-2 flex flex-col gap-2 pt-1 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="min-h-[48px] rounded-xl border border-stone-200 bg-white px-5 py-2.5 text-sm font-bold text-stone-700 transition hover:bg-stone-50"
              onClick={() => {
                if (savedAddresses.length > 0) {
                  setShowNewAddressForm(false);
                  const primaryIdx = savedAddresses.findIndex((a) => a.isPrimary);
                  const idx = primaryIdx >= 0 ? primaryIdx : 0;
                  handleSelect(idx);
                  return;
                }

                // If there are no saved addresses, keep the form reachable.
                setSelectedIndex(null);
                setShowNewAddressForm(true);
                setForm(emptyAddress);
                onAddressSelected?.(emptyAddress, false);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={isSaving}
              className="flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-orange-500 px-6 py-2.5 text-sm font-bold text-white shadow-md shadow-orange-300/40 transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-70"
              onClick={handleAddAddress}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                "Save address"
              )}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ShippingInfo;
